import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { Helmet } from "react-helmet";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import { getBackendUrl } from "../../config";
import wallfundo from "../../assets/f002.png";
import { ehAppAndroid } from "../../utils/appAndroid";

/* ─── CSS embutido — todas as cores via CSS variables ────────────────────── */
const AUTH_CSS = `
  .auth-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    box-sizing: border-box;
  }
  .auth-root {
    display: flex;
    width: 100%;
    max-width: 1040px;
    min-height: 600px;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 24px 80px rgba(0,0,0,0.5);
  }
  .auth-left {
    flex: 0 0 45%;
    position: relative;
    overflow: hidden;
    background: #000;
  }
  .auth-left__bg {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover;
    transition: opacity 1s ease-in-out;
  }
  .auth-left__overlay {
    position: absolute; inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.72) 100%);
  }
  .auth-left__content {
    position: relative; z-index: 1; height: 100%;
    display: flex; flex-direction: column;
    padding: 40px; box-sizing: border-box;
  }
  .auth-left__badge {
    display: inline-block;
    background: rgba(255,255,255,0.14);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.22);
    color: #fff; padding: 6px 16px; border-radius: 20px;
    font-size: 13px; font-weight: 600; letter-spacing: 0.02em;
  }
  .auth-left__spacer { flex: 1; }
  .auth-left__title { color: #fff; font-size: 2.1rem; font-weight: 700; margin: 0 0 10px; line-height: 1.2; }
  .auth-left__desc  { color: rgba(255,255,255,0.75); font-size: 0.97rem; margin: 0 0 24px; line-height: 1.55; }
  .auth-left__dots  { display: flex; gap: 8px; align-items: center; }
  .auth-left__dot   { height: 8px; border-radius: 4px; cursor: pointer; transition: all 0.35s ease; }

  /* ── Painel direito ── */
  .auth-right {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 48px 52px; box-sizing: border-box;
  }
  .auth-logo { margin-bottom: 28px; }
  .auth-logo__name {
    color: var(--auth-name, #fff);
    font-size: 1.5rem; font-weight: 800; letter-spacing: 0.04em;
  }
  .auth-welcome { text-align: center; margin-bottom: 28px; }
  .auth-welcome h1 {
    color: var(--auth-title, #fff);
    font-size: 1.7rem; font-weight: 700; margin: 0 0 8px;
  }
  .auth-welcome p {
    color: var(--auth-subtitle, #9ca3af);
    margin: 0; font-size: 0.94rem;
  }
  .auth-form { width: 100%; max-width: 400px; }
  .auth-label {
    display: block;
    color: var(--auth-label, #d1d5db);
    font-size: 13px; font-weight: 500; margin-bottom: 6px;
  }
  .auth-field-wrap { position: relative; margin-bottom: 16px; }
  .auth-field {
    width: 100%; box-sizing: border-box; padding: 13px 16px;
    background: var(--auth-input-bg, rgba(255,255,255,0.06));
    border: 1px solid var(--auth-input-border, rgba(255,255,255,0.11));
    border-radius: 10px;
    color: var(--auth-input-text, #fff);
    font-size: 15px; font-family: inherit; outline: none;
    transition: border-color 0.2s, background 0.2s;
  }
  .auth-field:focus {
    border-color: var(--auth-primary, #6366f1);
  }
  .auth-field::placeholder { color: var(--auth-placeholder, #6b7280); }
  .auth-field--pw { padding-right: 44px; }
  .auth-pw-toggle {
    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
    background: none; border: none;
    color: var(--auth-placeholder, #6b7280);
    cursor: pointer; display: flex; align-items: center; padding: 0;
  }
  .auth-check-row { display: flex; align-items: center; margin-bottom: 24px; gap: 8px; }
  .auth-check-row input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; flex-shrink: 0; }
  .auth-check-row label { color: var(--auth-subtitle, #9ca3af); font-size: 14px; cursor: pointer; }
  .auth-btn {
    width: 100%; padding: 14px;
    background: var(--auth-primary, #6366f1);
    color: var(--auth-btn-text, #fff);
    border: none; border-radius: 10px;
    font-size: 15px; font-weight: 600; cursor: pointer;
    font-family: inherit; transition: opacity 0.2s, transform 0.15s;
    letter-spacing: 0.01em;
  }
  .auth-btn:hover { opacity: 0.88; transform: translateY(-1px); }
  .auth-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .auth-signup { text-align: center; margin-top: 24px; font-size: 14px; color: var(--auth-subtitle, #9ca3af); }
  .auth-signup a {
    color: var(--auth-link, var(--auth-primary, #6366f1));
    font-weight: 600; text-decoration: none;
  }
  .auth-signup a:hover { text-decoration: underline; }
  .auth-footer { text-align: center; margin-top: 32px; color: var(--auth-subtitle, #4b5563); font-size: 12px; opacity: 0.6; }
  .auth-2fa-icon { text-align: center; margin-bottom: 16px; font-size: 3rem; }
  .auth-back-link {
    display: block; text-align: center; margin-top: 16px;
    color: var(--auth-link, var(--auth-primary, #6366f1));
    font-size: 14px; font-weight: 500;
    background: none; border: none; cursor: pointer;
    font-family: inherit; text-decoration: underline;
  }
  .auth-2fa-field { text-align: center; font-size: 1.8rem; letter-spacing: 0.5rem; }
  @media (max-width: 760px) {
    .auth-page { padding: 0; align-items: stretch; }
    .auth-root { border-radius: 0; min-height: 100vh; flex-direction: column; max-width: 100%; }
    .auth-left { display: none; }
    .auth-right { flex: 1; padding: 40px 24px; justify-content: flex-start; padding-top: 60px; }
  }
`;

/* ─── Utilitário ──────────────────────────────────────────────────────────── */
function fetchPublic(key) {
  const base = getBackendUrl();
  // Cada consulta desiste em 4 s: uma API lenta nao pode segurar a tela.
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const t = ctrl ? setTimeout(() => ctrl.abort(), 4000) : null;
  return fetch(`${base}/public-settings/${key}?token=wtV`, ctrl ? { signal: ctrl.signal } : undefined)
    .then(r => r.json())
    .catch(() => null)
    .finally(() => t && clearTimeout(t));
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
const Login = () => {
  // App mora em app.floow.chat: se abrir no dominio raiz, redireciona
  useEffect(() => {
    if (window.location.hostname === "floow.chat") {
      window.location.replace("https://app.floow.chat" + window.location.pathname + window.location.search);
    }
  }, []);
  const history = useHistory();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, primaryColorLight } = colorMode;
  const { handleLogin, handleVerify2FA, handleSetLoginOrigin } = useContext(AuthContext);
  const { getPublicSetting: getPS } = useSettings();

  /* form */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  /* signup */
  const [allowUserCreation, setAllowUserCreation] = useState(false);

  /* 2FA */
  const [twoFactorMode, setTwoFactorMode] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);

  /* slideshow */
  const [bgImages, setBgImages] = useState([wallfundo]);
  const [bgIndex, setBgIndex] = useState(0);
  const [slideContent, setSlideContent] = useState([{}, {}, {}]);

  /* branding / texts */
  const [siteLogo, setSiteLogo] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(primaryColorLight || "#6366f1");
  const [loginWelcomeTitle, setLoginWelcomeTitle] = useState("");
  const [loginWelcomeDesc, setLoginWelcomeDesc] = useState("");
  const [loginBtnLabel, setLoginBtnLabel] = useState("");
  const [loginRightBg, setLoginRightBg] = useState("");
  const [loginPageBg, setLoginPageBg] = useState("");
  const [loginBtnTextColor, setLoginBtnTextColor] = useState("");
  const [loginInputBg, setLoginInputBg] = useState("");
  const [loginInputBorder, setLoginInputBorder] = useState("");
  const [loginInputText, setLoginInputText] = useState("");
  const [loginLabelColor, setLoginLabelColor] = useState("");
  const [loginTitleColor, setLoginTitleColor] = useState("");
  const [loginSubtitleColor, setLoginSubtitleColor] = useState("");
  const [loginLinkColor, setLoginLinkColor] = useState("");
  const [loginAppNameColor, setLoginAppNameColor] = useState("");
  const [settingsReady, setSettingsReady] = useState(false);

  /* ── load settings ── */
  useEffect(() => {
    const base = getBackendUrl();

    // userCreation
    getPS("userCreation")
      .then(v => setAllowUserCreation(v !== "disabled"))
      .catch(() => {});

    // A tela so fica visivel quando as configuracoes chegam. Se a API estiver
    // lenta, isso deixava o login TRANSPARENTE por quase um minuto (24/09/2026:
    // parecia tela branca para todos os clientes). Depois de 2,5 s mostra do
    // jeito que estiver; o que chegar depois ainda aplica cores e imagens.
    const __mostrarMesmoAssim = setTimeout(() => setSettingsReady(true), 2500);
    // all public settings via Promise.all
    Promise.all([
      fetchPublic("termsImage"),
      fetchPublic("termsImage2"),
      fetchPublic("termsImage3"),
      fetchPublic("loginSlide1Tag"),
      fetchPublic("loginSlide1Title"),
      fetchPublic("loginSlide1Desc"),
      fetchPublic("loginSlide2Tag"),
      fetchPublic("loginSlide2Title"),
      fetchPublic("loginSlide2Desc"),
      fetchPublic("loginSlide3Tag"),
      fetchPublic("loginSlide3Title"),
      fetchPublic("loginSlide3Desc"),
      fetchPublic("loginWelcomeTitle"),
      fetchPublic("loginWelcomeDesc"),
      fetchPublic("loginBtnLabel"),
      fetchPublic("loginRightBg"),
      fetchPublic("loginPageBg"),
      fetchPublic("loginBtnBg"),
      fetchPublic("loginBtnTextColor"),
      fetchPublic("loginInputBg"),
      fetchPublic("loginInputBorder"),
      fetchPublic("loginInputText"),
      fetchPublic("loginLabelColor"),
      fetchPublic("loginTitleColor"),
      fetchPublic("loginSubtitleColor"),
      fetchPublic("loginLinkColor"),
      fetchPublic("loginAppNameColor"),
      fetchPublic("primaryColorLight"),
      fetchPublic("appLogoSite"),
    ]).then(([
      img1, img2, img3,
      s1tag, s1title, s1desc,
      s2tag, s2title, s2desc,
      s3tag, s3title, s3desc,
      wTitle, wDesc, btnLabel,
      rightBg, pageBg, btnBg,
      btnText, inputBg, inputBorder, inputText,
      labelColor, titleColor, subtitleColor, linkColor, nameColor,
      pColor, logoSite,
    ]) => {
      const imgs = [img1, img2, img3].filter(Boolean).map(f => `${base}/public/${f}`);
      if (imgs.length) setBgImages(imgs);

      setSlideContent([
        { tag: s1tag || "", title: s1title || "", desc: s1desc || "" },
        { tag: s2tag || "", title: s2title || "", desc: s2desc || "" },
        { tag: s3tag || "", title: s3title || "", desc: s3desc || "" },
      ]);

      if (wTitle) setLoginWelcomeTitle(wTitle);
      if (wDesc) setLoginWelcomeDesc(wDesc);
      if (btnLabel) setLoginBtnLabel(btnLabel);
      if (rightBg) setLoginRightBg(rightBg);
      if (pageBg) setLoginPageBg(pageBg);
      if (btnBg) setPrimaryColor(btnBg);
      else if (pColor) setPrimaryColor(pColor);
      if (btnText) setLoginBtnTextColor(btnText);
      if (inputBg) setLoginInputBg(inputBg);
      if (inputBorder) setLoginInputBorder(inputBorder);
      if (inputText) setLoginInputText(inputText);
      if (labelColor) setLoginLabelColor(labelColor);
      if (titleColor) setLoginTitleColor(titleColor);
      if (subtitleColor) setLoginSubtitleColor(subtitleColor);
      if (linkColor) setLoginLinkColor(linkColor);
      if (nameColor) setLoginAppNameColor(nameColor);
      if (logoSite) setSiteLogo(`${base}/public/${logoSite}`);
      clearTimeout(__mostrarMesmoAssim);
      setSettingsReady(true);
    });

    // remember me
    const saved = localStorage.getItem("auth_remembered_email");
    if (saved) { setEmail(saved); setRememberMe(true); }

    if (handleSetLoginOrigin) handleSetLoginOrigin("default");
  }, []);

  /* slideshow auto-play */
  useEffect(() => {
    if (bgImages.length <= 1) return;
    const t = setInterval(() => setBgIndex(i => (i + 1) % bgImages.length), 7000);
    return () => clearInterval(t);
  }, [bgImages]);

  /* ── handlers ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rememberMe) {
      localStorage.setItem("auth_remembered_email", email);
    } else {
      localStorage.removeItem("auth_remembered_email");
    }
    const result = await handleLogin({ email, password });
    if (result && result.requiresTwoFactor) {
      setTwoFactorMode(true);
      setTwoFactorEmail(result.email);
    } else if (result && result.success !== false) {
      if ("caches" in window) {
        caches.keys().then(names => names.forEach(n => caches.delete(n)));
      }
      window.location.reload(true);
    }
  };

  const handleSubmit2FA = async (e) => {
    e.preventDefault();
    setTwoFactorLoading(true);
    try {
      await handleVerify2FA({ email: twoFactorEmail, token: twoFactorCode });
      if ("caches" in window) {
        caches.keys().then(names => names.forEach(n => caches.delete(n)));
      }
      window.location.reload(true);
    } catch (_) {
      setTwoFactorCode("");
    }
    setTwoFactorLoading(false);
  };

  const primary = primaryColor || primaryColorLight || "#6366f1";
  const slide = slideContent[bgIndex] || {};

  return (
    <>
      <style>{AUTH_CSS}</style>
      <Helmet>
        <title>{appName || "Painel"}</title>
        {appLogoFavicon && <link rel="icon" href={appLogoFavicon} />}
      </Helmet>

      <div
        className="auth-page"
        style={{
          background: loginPageBg || "#0d1117",
          opacity: settingsReady ? 1 : 0,
          transition: "opacity 0.25s ease",
        }}
      >
      <div className="auth-root" style={{
          "--auth-primary":      primary,
          "--auth-btn-text":     loginBtnTextColor   || undefined,
          "--auth-input-bg":     loginInputBg        || undefined,
          "--auth-input-border": loginInputBorder    || undefined,
          "--auth-input-text":   loginInputText      || undefined,
          "--auth-label":        loginLabelColor     || undefined,
          "--auth-title":        loginTitleColor     || undefined,
          "--auth-subtitle":     loginSubtitleColor  || undefined,
          "--auth-link":         loginLinkColor      || undefined,
          "--auth-name":         loginAppNameColor   || undefined,
        }}>

        {/* ─── PAINEL ESQUERDO ─── */}
        <div className="auth-left">
          {/* imagens com crossfade */}
          {bgImages.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="auth-left__bg"
              style={{ opacity: i === bgIndex ? 1 : 0 }}
            />
          ))}
          <div className="auth-left__overlay" />

          <div className="auth-left__content">
            {/* badge do slide atual */}
            {slide.tag && (
              <div style={{ alignSelf: "flex-start" }}>
                <span className="auth-left__badge">{slide.tag}</span>
              </div>
            )}

            <div className="auth-left__spacer" />

            {/* título + descrição */}
            {slide.title && (
              <h2 className="auth-left__title">{slide.title}</h2>
            )}
            {slide.desc && (
              <p className="auth-left__desc">{slide.desc}</p>
            )}

            {/* dots de paginação */}
            {bgImages.length > 1 && (
              <div className="auth-left__dots">
                {bgImages.map((_, i) => (
                  <div
                    key={i}
                    className="auth-left__dot"
                    onClick={() => setBgIndex(i)}
                    style={{
                      width: i === bgIndex ? 32 : 8,
                      background: i === bgIndex ? primary : "rgba(255,255,255,0.35)",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── PAINEL DIREITO ─── */}
        <div
          className="auth-right"
          style={{ background: loginRightBg || "#111827" }}
        >
          {/* logo / nome do sistema */}
          {(siteLogo || appName) && (
            <div className="auth-logo">
              {siteLogo ? (
                <img
                  src={siteLogo}
                  alt={appName || "Logo"}
                  style={{ height: 62, maxWidth: 280, objectFit: "contain" }}
                />
              ) : (
                <span className="auth-logo__name">{appName}</span>
              )}
            </div>
          )}

          {/* título de boas-vindas */}
          <div className="auth-welcome">
            <h1>
              {twoFactorMode
                ? "Verificação em Duas Etapas"
                : (loginWelcomeTitle || "Bem-vindo(a) de volta!")}
            </h1>
            <p>
              {twoFactorMode
                ? "Digite o código de 6 dígitos do seu aplicativo autenticador."
                : (loginWelcomeDesc || "Acesse seu painel para gerenciar suas vendas.")}
            </p>
          </div>

          {/* ── formulário 2FA ── */}
          {twoFactorMode ? (
            <form className="auth-form" onSubmit={handleSubmit2FA}>
              <div className="auth-2fa-icon">🔐</div>
              <div className="auth-field-wrap">
                <label className="auth-label">Código 2FA</label>
                <input
                  className="auth-field auth-2fa-field"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  autoFocus
                  required
                />
              </div>
              <button
                type="submit"
                className="auth-btn"
                disabled={twoFactorCode.length !== 6 || twoFactorLoading}
              >
                {twoFactorLoading ? "Verificando..." : "Verificar"}
              </button>
              <button
                type="button"
                className="auth-back-link"
                onClick={() => { setTwoFactorMode(false); setTwoFactorEmail(""); setTwoFactorCode(""); }}
              >
                ← Voltar ao login
              </button>
            </form>
          ) : (
            /* ── formulário de login ── */
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-field-wrap">
                <label className="auth-label" htmlFor="auth-email">Seu E-mail</label>
                <input
                  id="auth-email"
                  className="auth-field"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>

              <div className="auth-field-wrap">
                <label className="auth-label" htmlFor="auth-password">Sua Senha</label>
                <div style={{ position: "relative" }}>
                  <input
                    id="auth-password"
                    className="auth-field auth-field--pw"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    className="auth-pw-toggle"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showPassword
                      ? <VisibilityOff style={{ fontSize: 20 }} />
                      : <Visibility style={{ fontSize: 20 }} />}
                  </button>
                </div>
              </div>

              <div className="auth-check-row" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    style={{ accentColor: primary }}
                  />
                  <label htmlFor="rememberMe">Lembrar de mim</label>
                </div>
                <RouterLink
                  to="/forgetpsw"
                  style={{ color: "var(--auth-link, var(--auth-primary, #6366f1))", fontSize: 13, fontWeight: 500, textDecoration: "none" }}
                >
                  Esqueci a senha
                </RouterLink>
              </div>

              <button type="submit" className="auth-btn">
                {loginBtnLabel || "Acessar Painel"}
              </button>

              {allowUserCreation && !ehAppAndroid() && (
                <div className="auth-signup">
                  Não tem uma conta?{" "}
                  <RouterLink to="/cadastro">Cadastre-se</RouterLink>
                </div>
              )}
            </form>
          )}

          <div className="auth-footer">
            © {new Date().getFullYear()} {appName || "Floow Chat"}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Login;
